"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { UsuarioSesion } from "./tipos";

// Tiempo mínimo entre dos consultas de sesión disparadas por la actividad de la persona.
const CONSULTA_CADA_MS = 60 * 1000;

type AuthContextValue = {
  usuario: UsuarioSesion | null;
  isAuthenticated: boolean;
  /** Va a Google. La sesión la abre el servidor al volver (`/api/auth/google/callback`). */
  loginWithGoogle: () => void;
  logout: () => Promise<void>;
  /** Reemplaza la sesión en pantalla (p. ej. con los datos personales recién guardados, sin recargar). */
  actualizarUsuario: (usuario: UsuarioSesion) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * La sesión vive en el servidor (cookie HttpOnly + tabla `sesiones`); el navegador solo recibe quién es.
 * `usuarioInicial` lo resuelve el layout raíz en el servidor, así que no hay un parpadeo de "cargando sesión".
 */
export function AuthProvider({ children, usuarioInicial }: { children: React.ReactNode; usuarioInicial: UsuarioSesion | null }) {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(usuarioInicial);

  // Cierre por inactividad (60 min, lo decide el servidor): la pantalla no se entera sola, así que con la
  // actividad (clic, tecla, volver a la pestaña) se consulta la sesión, como mucho una vez por minuto.
  // Consultar también la renueva, por eso solo se hace cuando la persona está haciendo algo.
  useEffect(() => {
    if (!usuario) return;
    let ultima = Date.now();

    async function consultar() {
      try {
        const respuesta = await fetch("/api/auth/session", { cache: "no-store" });
        if (!respuesta.ok) return; // el servidor no pudo responder: no es motivo para cerrar la sesión
        const cuerpo: { usuario: UsuarioSesion | null } = await respuesta.json();
        if (!cuerpo.usuario) setUsuario(null);
      } catch {
        // sin red: se reintenta con la próxima actividad
      }
    }

    function alActividad() {
      const ahora = Date.now();
      if (ahora - ultima < CONSULTA_CADA_MS) return;
      ultima = ahora;
      void consultar();
    }
    function alVolver() {
      if (document.visibilityState === "visible") alActividad();
    }

    window.addEventListener("pointerdown", alActividad);
    window.addEventListener("keydown", alActividad);
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      window.removeEventListener("pointerdown", alActividad);
      window.removeEventListener("keydown", alActividad);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [usuario]);

  const loginWithGoogle = useCallback(() => {
    // Navegación completa a propósito: es una ruta de servidor que redirige a Google, no una página de Next.
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination
    window.location.assign("/api/auth/google");
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } finally {
      // Aunque el servidor no responda, la pantalla sale; la sesión vencerá sola (8 h / 60 min).
      setUsuario(null);
    }
  }, []);

  const actualizarUsuario = useCallback((nuevo: UsuarioSesion) => setUsuario(nuevo), []);

  const value = useMemo(
    () => ({ usuario, isAuthenticated: usuario !== null, loginWithGoogle, logout, actualizarUsuario }),
    [usuario, loginWithGoogle, logout, actualizarUsuario],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Nombre de quien está operando, para dejar constancia en los historiales (órdenes, bodega, liquidación). */
export function useNombreActor(): string {
  return useAuth().usuario?.nombre ?? "Sin sesión";
}
